// src/factories/createAppData.ts
import {
  safeRound,
  variantToString,
  generateRowId,
  V2S_DATEONLY,
  V2S_TIME00
} from "@xone-web/types";
import {
  createDataCollection
} from "@xone-web/data";

// src/errors/XoneAppDataError.ts
var XONE_APPDATA_ERROR_CODES = {
  NOT_INITIALIZED: "XONE.APPDATA.NOT_INITIALIZED",
  NO_BACKEND_CLIENT: "XONE.APPDATA.NO_BACKEND_CLIENT",
  NO_UI_BRIDGE: "XONE.APPDATA.NO_UI_BRIDGE",
  LOGIN_FAILED: "XONE.APPDATA.LOGIN_FAILED",
  NOT_LOGGED_IN: "XONE.APPDATA.NOT_LOGGED_IN",
  UNKNOWN_COLLECTION: "XONE.APPDATA.UNKNOWN_COLLECTION",
  UNKNOWN_CLASS_ID: "XONE.APPDATA.UNKNOWN_CLASS_ID",
  INVALID_ARGUMENT: "XONE.APPDATA.INVALID_ARGUMENT",
  INCLUDE_FAILED: "XONE.APPDATA.INCLUDE_FAILED",
  SQL_FAILED: "XONE.APPDATA.SQL_FAILED",
  JWT_INVALID: "XONE.APPDATA.JWT_INVALID",
  /** Paridad mobile (`XoneFailWithMessageException`): `appData.failWithMessage(code, message)`. */
  FAIL_WITH_MESSAGE: "XONE.APPDATA.FAIL_WITH_MESSAGE"
};
var XoneAppDataError = class _XoneAppDataError extends Error {
  code;
  detail;
  constructor(message, init) {
    super(message);
    this.name = "XoneAppDataError";
    this.code = init.code;
    this.detail = init.detail;
    if (init.cause !== void 0) {
      this.cause = init.cause;
    }
    if (typeof Error.captureStackTrace === "function") {
      Error.captureStackTrace(
        this,
        _XoneAppDataError
      );
    }
  }
};

// src/auth/jwtClaims.ts
function base64UrlDecode(input) {
  let normalized = input.replace(/-/g, "+").replace(/_/g, "/");
  const pad = normalized.length % 4;
  if (pad === 2) normalized += "==";
  else if (pad === 3) normalized += "=";
  else if (pad !== 0) {
    throw new XoneAppDataError("JWT con payload base64url invalido.", {
      code: XONE_APPDATA_ERROR_CODES.JWT_INVALID
    });
  }
  if (typeof globalThis.Buffer !== "undefined") {
    const buf = globalThis.Buffer;
    return buf.from(normalized, "base64").toString("utf8");
  }
  if (typeof globalThis.atob === "function") {
    const atob = globalThis.atob;
    const bin = atob(normalized);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) {
      const ch = bin.charCodeAt(i);
      bytes[i] = ch;
    }
    return new TextDecoder("utf-8").decode(bytes);
  }
  throw new XoneAppDataError("Entorno sin Buffer ni atob para decodificar JWT.", {
    code: XONE_APPDATA_ERROR_CODES.JWT_INVALID
  });
}
function decodeJwtClaims(token) {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length !== 3) {
    return null;
  }
  const payload = parts[1];
  if (!payload) return null;
  try {
    const json = base64UrlDecode(payload);
    const obj = JSON.parse(json);
    if (typeof obj !== "object" || obj === null) return null;
    return obj;
  } catch (err) {
    throw new XoneAppDataError(
      `JWT con payload no parseable: ${err instanceof Error ? err.message : String(err)}`,
      {
        code: XONE_APPDATA_ERROR_CODES.JWT_INVALID,
        cause: err
      }
    );
  }
}

// src/auth/sessionMacros.ts
var SESSION_MACROS = [
  "TOKEN",
  "REFRESH_TOKEN",
  "ID_USUARIO",
  "ID_EMPRESA",
  "ROL",
  "PERMISOS"
];
var SENSITIVE_MACROS = /* @__PURE__ */ new Set([
  "TOKEN",
  "REFRESH_TOKEN",
  "ID_USUARIO",
  "ID_EMPRESA",
  "ROL",
  "PERMISOS",
  "REPLICATION_ID"
]);
function publishLoginMacros(macros, result) {
  macros.setGlobalMacro("TOKEN", result.token);
  if (result.refreshToken !== void 0) {
    macros.setGlobalMacro("REFRESH_TOKEN", result.refreshToken);
  }
  const claims = decodeJwtClaims(result.token);
  const userId = result.userId ?? claims?.sub;
  const enterpriseId = result.enterpriseId ?? claims?.enterprise;
  if (userId !== void 0 && userId !== null) {
    macros.setGlobalMacro("ID_USUARIO", String(userId));
  }
  if (enterpriseId !== void 0 && enterpriseId !== null) {
    macros.setGlobalMacro("ID_EMPRESA", String(enterpriseId));
  }
  const rol = result.rol ?? (typeof claims?.rol === "string" ? claims.rol : void 0);
  if (rol) {
    macros.setGlobalMacro("ROL", rol);
  }
  const permisos = result.permisos ?? (typeof claims?.permisos === "string" ? claims.permisos : void 0);
  if (permisos) {
    macros.setGlobalMacro("PERMISOS", permisos);
  }
}
function clearLoginMacros(macros) {
  for (const name of SESSION_MACROS) {
    macros.setGlobalMacro(name, null);
  }
}

// src/paths/Paths.ts
function normalizePath(input) {
  if (!input) return "/";
  let p = input.trim();
  if (!p.startsWith("/")) p = `/${p}`;
  p = p.replace(/\/+/g, "/");
  if (p.length > 1 && p.endsWith("/")) p = p.slice(0, -1);
  return p;
}
var Paths = class {
  _appPath;
  _filesPath;
  constructor(config = {}) {
    this._appPath = normalizePath(config.appPath ?? "/app");
    this._filesPath = normalizePath(config.filesPath ?? `${this._appPath}/files`);
  }
  getAppPath() {
    return this._appPath;
  }
  setAppPath(path) {
    this._appPath = normalizePath(path);
  }
  getFilesPath() {
    return this._filesPath;
  }
  setFilesPath(path) {
    this._filesPath = normalizePath(path);
  }
  /** Resuelve un path relativo contra `filesPath`. */
  resolveFilePath(relative) {
    const base = this._filesPath;
    if (!relative) return base;
    if (relative.startsWith("/")) return normalizePath(relative);
    return normalizePath(`${base}/${relative}`);
  }
};

// src/paths/Device.ts
var PHONE_MAX_WIDTH = 600;
var MINI_PHONE_MAX_WIDTH = 360;
var MID_PHONE_MAX_WIDTH = 480;
var TABLET_MIN_WIDTH = 600;
function detectDevice(input = {}) {
  let widthPx = input.widthPx;
  let heightPx = input.heightPx;
  if (widthPx === void 0 || heightPx === void 0) {
    const w = globalThis;
    if (typeof w.innerWidth === "number" && typeof globalThis.innerHeight === "number") {
      widthPx = widthPx ?? w.innerWidth;
      heightPx = heightPx ?? globalThis.innerHeight;
    } else if (w.screen) {
      widthPx = widthPx ?? w.screen.width;
      heightPx = heightPx ?? w.screen.height;
    }
  }
  const finalWidth = widthPx ?? 360;
  const finalHeight = heightPx ?? 640;
  const isPhone = finalWidth < TABLET_MIN_WIDTH;
  const isTablet = finalWidth >= TABLET_MIN_WIDTH;
  const isMiniPhone = isPhone && finalWidth <= MINI_PHONE_MAX_WIDTH;
  const isMidPhone = isPhone && finalWidth > MINI_PHONE_MAX_WIDTH && finalWidth <= MID_PHONE_MAX_WIDTH;
  const isHiPhone = isPhone && finalWidth > MID_PHONE_MAX_WIDTH && finalWidth < PHONE_MAX_WIDTH;
  const isVertical = finalHeight >= finalWidth;
  const isHorizontal = !isVertical;
  const base = {
    isPhone,
    isMiniPhone,
    isMidPhone,
    isHiPhone,
    isTablet,
    isWatch: false,
    isWatchRound: false,
    isWatchSquare: false,
    isVertical,
    isHorizontal
  };
  return input.override ? { ...base, ...input.override } : base;
}

// src/factories/createObjectRegistry.ts
var CLASS_ID_DEFAULTS = Object.freeze({
  FileSystemObject: { async: true },
  FileManager: { async: true },
  OAuth2: { async: true },
  RTCClient: { async: true },
  WebSocket: { async: true },
  BarcodeGenerator: { async: true },
  XOnePDF: { async: true },
  XOnePrinter: { async: true },
  CodeScanner: { async: true },
  XOneNFC: { async: true },
  XOneOCR: { async: true },
  ImageDrawing: { async: true },
  WifiManager: { async: true },
  AndroidIntent: { async: true },
  GpsTools: { async: true },
  DebugTools: { async: false },
  IniParser: { async: false },
  INI: { async: false },
  DOMParser: { async: false },
  Bundle: { async: false },
  Encoder: { async: false },
  Crypto: { async: false }
});
var CreateObjectRegistry = class {
  entries = /* @__PURE__ */ new Map();
  register(classId, entry) {
    if (!classId || typeof classId !== "string") {
      throw new XoneAppDataError(`classId invalido: ${String(classId)}`, {
        code: XONE_APPDATA_ERROR_CODES.INVALID_ARGUMENT
      });
    }
    this.entries.set(classId, entry);
  }
  has(classId) {
    return this.entries.has(classId);
  }
  /**
   * Indica si el classId, segun el registry o la tabla de defaults, abre
   * un handle async. Si no se conoce, retorna `false` (sync por defecto).
   */
  isAsync(classId) {
    const reg = this.entries.get(classId);
    if (reg) return reg.async;
    return CLASS_ID_DEFAULTS[classId]?.async === true;
  }
  /**
   * Construye el objeto. Si el factory registrado es async, devuelve la
   * promesa tal cual; si es sync, la envuelve segun la documentacion del
   * plan §3.1 ("Sync para los puramente computacionales").
   */
  create(classId, args) {
    const reg = this.entries.get(classId);
    if (!reg) {
      throw new XoneAppDataError(`classId desconocido: '${classId}'`, {
        code: XONE_APPDATA_ERROR_CODES.UNKNOWN_CLASS_ID,
        detail: { classId }
      });
    }
    return reg.factory(...args);
  }
};

// src/factories/createAppData.ts
var StorageAdapterFromXoneDatabase = class {
  constructor(storage) {
    this.storage = storage;
  }
  storage;
  async executeSql(sql, params) {
    const arr = Array.isArray(params) ? params : [];
    const result = await this.storage.db.executeSql(sql, arr);
    if (Array.isArray(result)) {
      return {
        rows: result,
        affectedRows: 0
      };
    }
    return {
      rows: [],
      affectedRows: result
    };
  }
  async transaction(fn) {
    return this.storage.db.transaction(async (tx) => {
      const adapter = {
        executeSql: async (sql, params) => {
          const arr = Array.isArray(params) ? params : [];
          const result = await tx.executeSql(sql, arr);
          if (Array.isArray(result)) {
            return {
              rows: result,
              affectedRows: 0
            };
          }
          return { rows: [], affectedRows: result };
        },
        transaction: async (_inner) => {
          return _inner(adapter);
        }
      };
      return fn(adapter);
    });
  }
  isReady() {
    return this.storage.db.isOpen();
  }
};
function createAppData(config) {
  if (!config || typeof config !== "object") {
    throw new TypeError("createAppData requiere un objeto de configuracion.");
  }
  if (!config.macros) {
    throw new TypeError("XoneAppDataConfig.macros es obligatorio.");
  }
  if (!config.runtime) {
    throw new TypeError("XoneAppDataConfig.runtime es obligatorio.");
  }
  if (!config.storage) {
    throw new TypeError("XoneAppDataConfig.storage es obligatorio.");
  }
  const macros = config.macros;
  const runtime = config.runtime;
  const storage = config.storage;
  const bus = config.bus;
  const cryptoLib = config.crypto;
  const backendClient = config.backendClient;
  const uiBridge = config.uiBridge;
  const collectionsDefs = config.collectionsDefinitions ?? {};
  const registry = config.createObjectRegistry ?? new CreateObjectRegistry();
  const storageAdapter = new StorageAdapterFromXoneDatabase(storage);
  if (!macros.getGlobalMacro("DEVICE_ID")) {
    macros.setGlobalMacro("DEVICE_ID", generateRowId());
  }
  if (config.appName) {
    macros.setGlobalMacro("APP_NAME", config.appName);
  }
  if (config.defaultLanguage) {
    macros.setGlobalMacro("LANGUAGE", config.defaultLanguage);
  }
  if (config.tablePrefix !== void 0) {
    macros.setGlobalMacro("PREF", config.tablePrefix);
  }
  const paths = new Paths({
    ...config.appPath !== void 0 && { appPath: config.appPath },
    ...config.filesPath !== void 0 && { filesPath: config.filesPath }
  });
  const device = config.device ?? detectDevice();
  const collectionsCache = /* @__PURE__ */ new Map();
  const loadedCss = /* @__PURE__ */ new Set();
  const stack = [];
  let replicating = false;
  let replicationId = macros.getGlobalMacro("REPLICATION_ID") ?? 0;
  let connString = "";
  let debugMode = false;
  let lastError = null;
  let currentEnterprise = null;
  function buildCollection(name, def) {
    const resolved = def ? { ...def } : {
      name,
      tableName: name.toLowerCase(),
      tablePrefix: config.tablePrefix,
      fields: [],
      contents: []
    };
    const coll = createDataCollection({
      name: resolved.name,
      definition: resolved,
      storage: storageAdapter,
      macros,
      ...bus ? { bus } : {},
      appData: api
    });
    return coll;
  }
  function getCollectionSync(name) {
    const cached = collectionsCache.get(name);
    if (cached) return cached;
    const def = collectionsDefs[name];
    if (!def) {
      return null;
    }
    const coll = buildCollection(name, def);
    collectionsCache.set(name, coll);
    return coll;
  }
  async function getCollectionAsync(name) {
    let coll = collectionsCache.get(name) ?? null;
    if (!coll) {
      const def = collectionsDefs[name];
      if (!def) return null;
      coll = buildCollection(name, def);
      collectionsCache.set(name, coll);
    }
    await coll.loadAll();
    return coll;
  }
  function captureError(err) {
    if (err instanceof XoneAppDataError) {
      const captured = {
        code: err.code,
        message: err.message
      };
      if (err.stack) captured.stack = err.stack;
      return captured;
    }
    if (err instanceof Error) {
      const code = err.code ?? "XONE.APPDATA.UNKNOWN";
      const captured = {
        code,
        message: err.message
      };
      if (err.stack) captured.stack = err.stack;
      return captured;
    }
    return { code: "XONE.APPDATA.UNKNOWN", message: String(err) };
  }
  function trackError(op) {
    return op().catch((err) => {
      lastError = captureError(err);
      throw err;
    });
  }
  async function persistMacroIfSensitive(name, value) {
    if (!SENSITIVE_MACROS.has(name)) return;
    const persist = backendClient?.auth?.persistMacro;
    if (typeof persist === "function") {
      try {
        await persist.call(backendClient.auth, name, value);
      } catch {
      }
    }
  }
  const api = {
    // -------- Colecciones --------
    getCollection: ((name, load) => {
      if (load === true) return getCollectionAsync(name);
      return getCollectionSync(name);
    }),
    async createCollection(name, definition) {
      const existing = collectionsCache.get(name);
      if (existing) return existing;
      const def = definition ?? collectionsDefs[name];
      const coll = buildCollection(name, def);
      collectionsCache.set(name, coll);
      return coll;
    },
    getCurrentEnterprise() {
      return currentEnterprise;
    },
    getCollectionCount() {
      return collectionsCache.size;
    },
    clearCaches() {
      collectionsCache.clear();
      for (const css of loadedCss) loadedCss.delete(css);
      lastError = null;
    },
    /**
     * Paridad XOne 1.3.0 (`XoneApplication#bind`): registra callback para evento
     * global. Acepta minimo 2 parametros: `(eventKey, callback, ...extras)`. Cada
     * llamada anade un nuevo callback (no sustituye). En MVP es no-op documentado:
     * los listeners reales se gestionan via `04-events` (`bus.subscribe`).
     */
    bind(eventKey, _callback, ..._extras) {
      if (!eventKey || typeof eventKey !== "string") {
        throw new XoneAppDataError("appData.bind: eventKey vacio.", {
          code: XONE_APPDATA_ERROR_CODES.INVALID_ARGUMENT
        });
      }
    },
    // -------- Macros (SYNC) --------
    setGlobalMacro(name, value) {
      const previous = macros.setGlobalMacro(name, value);
      void persistMacroIfSensitive(name, value === null || value === void 0 ? null : String(value));
      return previous;
    },
    getGlobalMacro(name) {
      return macros.getGlobalMacro(name);
    },
    getAllGlobalMacros() {
      return macros.getAllGlobalMacros();
    },
    getAllowedEnterprises() {
      return macros.getGlobalMacro("ALLOWED_ENTERPRISES") ?? "";
    },
    getAllowedUsers() {
      return macros.getGlobalMacro("ALLOWED_USERS") ?? "";
    },
    getVisualConditions() {
      return macros.getGlobalMacro("VISUAL_CONDITIONS") ?? "";
    },
    // -------- Sesion (ASYNC) --------
    /**
     * Paridad XOne 1.3.0 (`XoneApplication#login`): la firma canonica mobile recibe
     * UN unico objeto `{userName, password, isGoogleSignOn, noAuth, entryPoint,
     * onLoginSuccessful, onLoginFailed, onLoginCallResults}`. El SPA web tambien
     * acepta la firma posicional `login(user, password, enterprise)` por compat
     * con tests y codigo generado previo.
     */
    login: (async (arg1, arg2, arg3) => {
      if (!backendClient?.auth) {
        throw new XoneAppDataError(
          "No hay backendClient.auth inyectado. login no disponible.",
          { code: XONE_APPDATA_ERROR_CODES.NO_BACKEND_CLIENT }
        );
      }
      const opts = typeof arg1 === "string" ? {
        userName: arg1,
        ...arg2 !== void 0 ? { password: arg2 } : {},
        ...arg3 !== void 0 ? { enterprise: arg3 } : {}
      } : arg1;
      if (opts.isGoogleSignOn !== true && opts.noAuth !== true && !opts.userName) {
        throw new XoneAppDataError(
          "login: userName es obligatorio salvo Google Sign-On o noAuth.",
          { code: XONE_APPDATA_ERROR_CODES.INVALID_ARGUMENT }
        );
      }
      return trackError(async () => {
        const deviceId = opts.deviceId ?? macros.getGlobalMacro("DEVICE_ID") ?? void 0;
        const loginInput = {
          user: opts.userName ?? "",
          password: opts.password ?? ""
        };
        if (opts.enterprise !== void 0) {
          loginInput.enterprise = opts.enterprise;
        }
        if (deviceId) {
          loginInput.deviceId = deviceId;
        }
        if (opts.isGoogleSignOn) {
          loginInput.isGoogleSignOn = true;
        }
        if (opts.noAuth) {
          loginInput.noAuth = true;
        }
        if (opts.entryPoint) {
          loginInput.entryPoint = opts.entryPoint;
        }
        try {
          const result = await backendClient.auth.login(loginInput);
          publishLoginMacros(macros, result);
          if (result.enterpriseId !== void 0) {
            currentEnterprise = { CODIGO: result.enterpriseId };
          }
          if (bus) {
            await bus.emitCollection("onlogon", { ThisDataColl: api });
          }
          if (opts.onLoginSuccessful) {
            try {
              await opts.onLoginSuccessful();
            } catch {
            }
          }
        } catch (err) {
          if (opts.onLoginFailed) {
            const msg = err instanceof Error ? err.message : String(err);
            try {
              await opts.onLoginFailed(msg);
            } catch {
            }
          }
          throw err;
        }
      });
    }),
    async logout() {
      return trackError(async () => {
        if (backendClient?.auth?.logout) {
          try {
            await backendClient.auth.logout();
          } catch {
          }
        }
        clearLoginMacros(macros);
        currentEnterprise = null;
        collectionsCache.clear();
        if (bus) {
          await bus.emitCollection("onlogoff", { ThisDataColl: api });
        }
      });
    },
    /**
     * Paridad XOne 1.3.0 (`XoneApplication#exit`): acepta 0-2 parametros:
     * `resultCode` (string: "OK"|"CANCELLED"|"FIRST_USER") e `intent` (`AndroidIntent`).
     * En web mapeamos a un `code` opcional que el `onExit` consume.
     */
    async exit(resultCode, _intent) {
      try {
        if (bus) {
          await bus.emitObject("unload", { self: api });
        }
      } catch {
      }
      if (config.onExit) {
        const codeNumber = typeof resultCode === "number" ? resultCode : resultCode === "OK" ? 0 : resultCode === "CANCELLED" ? 1 : resultCode === "FIRST_USER" ? 2 : void 0;
        await config.onExit(codeNumber);
      }
    },
    async restart(resultCode, intent) {
      await api.exit(resultCode, intent);
      if (config.onRestart) {
        await config.onRestart();
        return;
      }
      const loc = globalThis.location;
      if (loc && typeof loc.reload === "function") {
        loc.reload();
      }
    },
    // -------- Conexiones / SQL --------
    getConnection(_name) {
      return storage.db;
    },
    getConnString() {
      return connString;
    },
    setConnString(value) {
      connString = String(value ?? "");
    },
    async executeSql(sql, params) {
      return trackError(async () => {
        const { replaceMacrosSql } = await import("@xone-web/macros");
        const developed = replaceMacrosSql(sql, {
          globalMacros: macros.getAllGlobalMacros()
        });
        const arr = Array.isArray(params) ? params : [];
        const result = await storage.db.executeSql(developed, arr);
        return result;
      });
    },
    // -------- Replica --------
    isReplicating() {
      return replicating;
    },
    setIsReplicating(value) {
      replicating = Boolean(value);
    },
    getReplicationId() {
      const macro = macros.getGlobalMacro("REPLICATION_ID");
      if (macro !== null) return macro;
      return replicationId;
    },
    // -------- Rutas --------
    getAppName() {
      return config.appName ?? "";
    },
    getAppPath() {
      return paths.getAppPath();
    },
    getFilesPath() {
      return paths.getFilesPath();
    },
    setFilesPath(path) {
      paths.setFilesPath(path);
    },
    /**
     * Paridad XOne 1.3.0 (`XoneApplication#loadIncludeFile`):
     *   `loadIncludeFile(fileName, language, [encoding], [delayCompilation], [compile])`.
     * En el SPA web:
     *   - `language` es opcional (default `"javascript"`).
     *   - Si el segundo argumento es un STRING que parece codigo fuente (no `"javascript"|
     *     "vbscript"|"java"`), lo tratamos como `source` precargado por el bundler
     *     (extension web). Esto preserva la sobrecarga previa `loadIncludeFile(path, source)`.
     *   - Si el lenguaje no es `"javascript"`, lanzamos error (el SPA web solo ejecuta JS).
     */
    loadIncludeFile: (async (fileName, arg2, _encoding, _delayCompilation, _compile) => {
      return trackError(async () => {
        let language;
        let source;
        if (arg2 === void 0) {
          language = "javascript";
        } else if (arg2 === "javascript" || arg2 === "vbscript" || arg2 === "java") {
          language = arg2;
        } else {
          source = arg2;
          language = "javascript";
        }
        if (language !== "javascript") {
          throw new XoneAppDataError(
            `loadIncludeFile: lenguaje '${language}' no soportado en el SPA web (solo javascript).`,
            { code: XONE_APPDATA_ERROR_CODES.INCLUDE_FAILED, detail: { fileName, language } }
          );
        }
        let code = source;
        if (code === void 0) {
          if (!config.fetchSource) {
            throw new XoneAppDataError(
              `No se puede cargar '${fileName}' sin source ni fetchSource.`,
              { code: XONE_APPDATA_ERROR_CODES.INCLUDE_FAILED, detail: { path: fileName } }
            );
          }
          try {
            code = await config.fetchSource(fileName);
          } catch (err) {
            throw new XoneAppDataError(
              `Fallo al descargar include '${fileName}': ${err instanceof Error ? err.message : String(err)}`,
              { code: XONE_APPDATA_ERROR_CODES.INCLUDE_FAILED, cause: err }
            );
          }
        }
        runtime.loadIncludeFile(fileName, code);
      });
    }),
    /**
     * Paridad XOne 1.3.0 (`XoneApplication#loadCssFile`):
     *   `loadCssFile(name, [encoding], [conditions], [strictMode])` posicional o
     *   un unico objeto `{name, encoding, conditions, strictMode}`.
     * Implementacion MVP: registra el nombre en el set `loadedCss`. La aplicacion
     * efectiva del CSS la hace `@xone-web/css-engine` consumiendo este set.
     */
    async loadCssFile(name, _encoding, _conditions, _strictMode) {
      if (!name) {
        throw new XoneAppDataError("loadCssFile: name vacio.", {
          code: XONE_APPDATA_ERROR_CODES.INVALID_ARGUMENT
        });
      }
      loadedCss.add(name);
    },
    async unloadCssFile(name) {
      loadedCss.delete(name);
    },
    // -------- Dispositivo --------
    isPhone() {
      return device.isPhone;
    },
    isMiniPhone() {
      return device.isMiniPhone;
    },
    isMidPhone() {
      return device.isMidPhone;
    },
    isHiPhone() {
      return device.isHiPhone;
    },
    isTablet() {
      return device.isTablet;
    },
    isWatch() {
      return device.isWatch;
    },
    isWatchRound() {
      return device.isWatchRound;
    },
    isWatchSquare() {
      return device.isWatchSquare;
    },
    isVertical() {
      return device.isVertical;
    },
    isHorizontal() {
      return device.isHorizontal;
    },
    isDebugMode() {
      return debugMode;
    },
    setDebugMode(value) {
      const prev = debugMode;
      debugMode = Boolean(value);
      return prev;
    },
    // -------- Stack --------
    pushValue(value) {
      stack.push(value);
      return value;
    },
    popValue() {
      return stack.pop();
    },
    pushValueAndExit(value) {
      stack.push(value);
      return value;
    },
    writeConsoleString(message) {
      const text = message instanceof Error ? message.message : String(message);
      console.log(text);
    },
    // -------- Miscelaneo --------
    /**
     * Paridad XOne 1.3.0 (`XoneApplication#encryptString`): `(plain, type)` con
     * `type` numerico obligatorio en mobile. En el SPA web aceptamos:
     *   - `type` numerico (codigo de algoritmo) -> hint para `cryptoLib`.
     *   - `type === 'corporate'` (string) -> delega al backend `CryptoService`.
     *   - `type` ausente -> usa `cryptoLib` con CRYPTO_KEY (compat legacy).
     */
    async encryptString(plain, type) {
      if (type === "corporate") {
        if (!backendClient?.crypto) {
          throw new XoneAppDataError(
            "Encryption corporate requiere backendClient.crypto.",
            { code: XONE_APPDATA_ERROR_CODES.NO_BACKEND_CLIENT }
          );
        }
        return backendClient.crypto.encrypt({ value: plain, algorithm: String(type) });
      }
      if (!cryptoLib) {
        throw new XoneAppDataError(
          "No hay XoneCrypto inyectado y el tipo no es corporate.",
          { code: XONE_APPDATA_ERROR_CODES.INVALID_ARGUMENT, detail: { type } }
        );
      }
      const key = macros.getGlobalMacro("CRYPTO_KEY") ?? "xone-default-key";
      return cryptoLib.encrypt(plain, key);
    },
    async decryptString(cipher, type) {
      if (type === "corporate") {
        if (!backendClient?.crypto) {
          throw new XoneAppDataError(
            "Decryption corporate requiere backendClient.crypto.",
            { code: XONE_APPDATA_ERROR_CODES.NO_BACKEND_CLIENT }
          );
        }
        return backendClient.crypto.decrypt({ value: cipher, algorithm: String(type) });
      }
      if (!cryptoLib) {
        throw new XoneAppDataError(
          "No hay XoneCrypto inyectado y el tipo no es corporate.",
          { code: XONE_APPDATA_ERROR_CODES.INVALID_ARGUMENT, detail: { type } }
        );
      }
      const key = macros.getGlobalMacro("CRYPTO_KEY") ?? "xone-default-key";
      return cryptoLib.decrypt(cipher, key);
    },
    variantToString(value, flags) {
      return variantToString(value, flags);
    },
    safeRound(value, decimals) {
      return safeRound(value, decimals);
    },
    createObject(classId, ...args) {
      if (!classId || typeof classId !== "string") {
        throw new XoneAppDataError(`classId invalido: ${String(classId)}`, {
          code: XONE_APPDATA_ERROR_CODES.INVALID_ARGUMENT
        });
      }
      if (registry.has(classId)) {
        return registry.create(classId, args);
      }
      if (classId === "FileSystemObject" || classId === "FileManager") {
        return Promise.resolve(storage.createFileSystemObject());
      }
      throw new XoneAppDataError(`classId desconocido: '${classId}'`, {
        code: XONE_APPDATA_ERROR_CODES.UNKNOWN_CLASS_ID,
        detail: { classId }
      });
    },
    getReservedObject(_name) {
      return null;
    },
    error() {
      return lastError;
    },
    /**
     * Paridad XOne 1.3.0 (`XoneApplication#failWithMessage`): requiere 2 parametros
     * `(code, message)`. Si solo se pasa el `message`, asumimos `code = 0` por compat.
     * Lanza `XoneFailWithMessageException` (modelado como `XoneAppDataError` con
     * `code = APPDATA.FAIL_WITH_MESSAGE` y detail `{xoneCode}`).
     */
    failWithMessage: ((arg1, arg2) => {
      let xoneCode;
      let message;
      if (typeof arg1 === "number") {
        xoneCode = arg1;
        message = arg2 ?? "";
      } else {
        xoneCode = 0;
        message = arg1;
      }
      throw new XoneAppDataError(message, {
        code: XONE_APPDATA_ERROR_CODES.FAIL_WITH_MESSAGE,
        detail: { xoneCode }
      });
    }),
    /**
     * Paridad XOne 1.3.0 (`XoneApplication#registerPush`): 1-3 parametros.
     * Variantes:
     *   - `registerPush(callback)` — registra callback `onPushRegistered`.
     *   - `registerPush({onSuccess, onFailure, onPushReceived})` — registra mapa.
     *   - `registerPush(senderId: string)` — historico, ignorado.
     * Extension SPA web:
     *   - `registerPush(token, platform?, metadata?)` — registra el token push del
     *     navegador contra el backend `PushNotificationService`.
     */
    registerPush: (async (tokenOrCallbackOrOptions, platformOrExtra, metadata) => {
      if (typeof tokenOrCallbackOrOptions === "function" || typeof tokenOrCallbackOrOptions === "object" && tokenOrCallbackOrOptions !== null && ("onSuccess" in tokenOrCallbackOrOptions || "onFailure" in tokenOrCallbackOrOptions || "onPushReceived" in tokenOrCallbackOrOptions)) {
        return;
      }
      if (typeof tokenOrCallbackOrOptions !== "string") {
        return;
      }
      const token = tokenOrCallbackOrOptions;
      if (!backendClient?.pushNotification) {
        throw new XoneAppDataError(
          "No hay backendClient.pushNotification inyectado.",
          { code: XONE_APPDATA_ERROR_CODES.NO_BACKEND_CLIENT }
        );
      }
      const input = {
        token
      };
      if (typeof platformOrExtra === "string") {
        input.platform = platformOrExtra;
      }
      if (metadata !== void 0) input.metadata = metadata;
      await backendClient.pushNotification.register(input);
    }),
    /**
     * Paridad XOne 1.3.0 (`XoneApplication#toString`): devuelve
     * `"App.<NombreApp>.@<hashHex>"`.
     */
    toString() {
      const name = config.appName ?? "";
      const hexBase = (macros.getGlobalMacro("DEVICE_ID") ?? "").slice(0, 8);
      return `App.${name}.@${hexBase}`;
    },
    V2S_DATEONLY,
    V2S_TIME00
  };
  void uiBridge;
  return api;
}
export {
  CLASS_ID_DEFAULTS,
  CreateObjectRegistry,
  Paths,
  SENSITIVE_MACROS,
  SESSION_MACROS,
  XONE_APPDATA_ERROR_CODES,
  XoneAppDataError,
  clearLoginMacros,
  createAppData,
  decodeJwtClaims,
  detectDevice,
  publishLoginMacros
};
//# sourceMappingURL=index.js.map